import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../../../lib/http";
import { formHandleFromHost } from "../../../lib/site/config";
import { usePublicFormHead } from "../../shared/publicFormHead";
import PublicFormShell from "../../shared/components/PublicFormShell";
import PublicFormSkeleton from "../../shared/components/PublicFormSkeleton";
import PublicFormSplash, { useSplash } from "../../shared/components/PublicFormSplash";
import PublicEnquiryFields from "../components/PublicEnquiryFields";
import PublicEnquirySubmitted from "../components/PublicEnquirySubmitted";
import PublicEnquiryWelcome from "../components/PublicEnquiryWelcome";
import { usePublicEnquiryForm } from "../hooks/usePublicEnquiryForm";
import {
  submitPublicEnquiry,
  toFieldErrors,
  toPublicEnquiryPayload,
} from "../services/publicEnquiryService";
import type { PublicEnquiryField } from "../publicEnquiryValidation";
import type {
  PublicBrand,
  PublicEnquiryAnswers,
} from "../types/enquiries.types";

/**
 * A practitioner's shared enquiry form, at `<their-name>.naurist.com/form` —
 * or `connect.naurist.com/<their-name>`, which is the same form by the older
 * shape.
 *
 * Three steps behind one URL — the welcome, the questions, the thank-you —
 * rather than three routes. Nothing here is a place somebody would want to
 * link to, bookmark, or return to: the form is a single sitting, and a URL per
 * step would invite a reload landing halfway through one with the answers gone.
 *
 * Renders with nobody signed in. There is no Clerk session on this screen, no
 * workspace, no toast provider and no query client — see `App`, which mounts
 * the forms site above all of it. Anything imported here has to work without
 * them.
 */

type Step = "welcome" | "form" | "submitted";

/** Stands in until the practitioner's own brand has been read. */
const NO_BRAND: PublicBrand = { name: "", logoUrl: "" };

/**
 * What a screen with nothing to show says.
 *
 * Never names the mechanism. An unclaimed name, a name whose owner switched
 * sharing off, and a name that was changed yesterday are all the same message —
 * the state of somebody else's account is not a stranger's business, and
 * confirming that a link was once real is itself telling them something.
 */
const Notice: React.FC<{
  title: string;
  detail: string;
  action?: { label: string; onClick: () => void };
}> = ({ title, detail, action }) => (
  <div className="sm:text-center">
    <h1 className="text-[26px] sm:text-[32px] font-semibold text-[#0A4A52]">
      {title}
    </h1>
    <p className="mt-3 text-[15px] leading-7 text-ink-muted">{detail}</p>
    {action && (
      <button
        type="button"
        onClick={action.onClick}
        className="mt-6 text-[14px] font-medium text-brand hover:underline"
      >
        {action.label}
      </button>
    )}
  </div>
);

const PublicEnquiryFormPage: React.FC = () => {
  const { handle: pathHandle } = useParams<{ handle: string }>();
  // The hostname on a practitioner's own subdomain, which is where every link
  // handed out points; the path only on the shared host and on localhost,
  // where the hostname holds no name to read — see `formHandleFromHost`. The
  // host wins where there is one, so nothing appended to a subdomain address
  // can serve somebody else's form under their name.
  const handle = formHandleFromHost() || pathHandle || "";
  const { status, form, reload } = usePublicEnquiryForm(handle);

  // The practitioner's mark alone for the first three seconds, in front of all
  // of this — see `PublicFormSplash`. Cut short where the read came back with
  // nothing to greet anybody with, since a splash in front of "this form is not
  // available" is three seconds of blank white and then an apology.
  const splashing = useSplash(status === "missing" || status === "failed");

  const [step, setStep] = useState<Step>("welcome");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  // What the server said about one answer rather than about the submission —
  // an email address it already holds an enquiry from, most of all.
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<PublicEnquiryField, string>>
  >({});

  const brand = form?.brand ?? NO_BRAND;

  // The tab and the social card belong to the practitioner, not to us — see
  // `usePublicFormHead`, which also leaves the tab without Naurist's mark on it.
  usePublicFormHead(brand.name, "Enquiry form");

  const send = async (answers: PublicEnquiryAnswers) => {
    if (!form) return;

    const payload = toPublicEnquiryPayload(answers, form);
    if (!payload) {
      // A goal or life style that no longer matches a row in the list this
      // form was served with — the catalog moved while the page sat open.
      setSubmitError(
        "This form is out of date. Please reload the page and try again."
      );
      return;
    }

    setSubmitting(true);
    setSubmitError(undefined);
    setFieldErrors({});

    try {
      await submitPublicEnquiry(handle, payload);
      setStep("submitted");
    } catch (error) {
      // A complaint about one answer goes under that answer — an email address
      // this practitioner already holds an enquiry from is the case this is
      // really for. Only where the server named no field does the whole form
      // get a message, because then there is nothing to point at.
      const fields = toFieldErrors(error);
      if (Object.keys(fields).length > 0) {
        setFieldErrors(fields);
      } else {
        setSubmitError(
          error instanceof ApiError
            ? error.message
            : "We could not reach the server. Please check your connection and try again."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** Withdraws the server's verdict on a field the moment it is edited. */
  const clearFieldError = (field: PublicEnquiryField) =>
    setFieldErrors((current) => {
      if (!(field in current)) return current;
      const { [field]: _dropped, ...rest } = current;
      return rest;
    });

  if (splashing) return <PublicFormSplash brand={brand} />;

  if (status === "loading") {
    return (
      // The welcome screen's own frame, to the prop: white rather than the
      // form's tint, the mark inline at the head of a centred block, and the
      // phone's wide gutter. What arrives lands in the same place it stood.
      <PublicFormShell
        brand={NO_BRAND}
        loading
        centered
        lockup="inline"
        gutter="wide"
        fit
      >
        <PublicFormSkeleton />
      </PublicFormShell>
    );
  }

  if (status === "missing") {
    return (
      <PublicFormShell brand={NO_BRAND} centered>
        <Notice
          title="This form is not available"
          detail="The link may have changed. Please ask for a new one."
        />
      </PublicFormShell>
    );
  }

  if (status === "failed" || !form) {
    return (
      <PublicFormShell brand={NO_BRAND} centered>
        <Notice
          title="Something went wrong"
          detail="We could not reach the server. Please check your connection and try again."
          action={{ label: "Try again", onClick: reload }}
        />
      </PublicFormShell>
    );
  }

  if (step === "welcome") {
    return (
      // The mark travels with the greeting here rather than sitting at the top
      // of the page — see `lockup` on the shell. Nothing else is on this
      // screen, so the two belong to each other.
      // `fit`: the greeting, the sentence and the button are the whole of the
      // screen, so there is nowhere for a scroll to go and it does not get one
      // — not even the few pixels a phone's own toolbars would otherwise steal.
      <PublicFormShell
        brand={brand}
        centered
        lockup="inline"
        gutter="wide"
        fit
      >
        <PublicEnquiryWelcome onBegin={() => setStep("form")} />
      </PublicFormShell>
    );
  }

  if (step === "submitted") {
    return (
      // Not `centered`: this screen centres its own message and keeps the
      // closing line at the foot of the page, which it can only do if the
      // column it sits in leaves the height to it.
      <PublicFormShell brand={brand}>
        <PublicEnquirySubmitted brandName={brand.name} />
      </PublicFormShell>
    );
  }

  return (
    // The mark sits with the form rather than above it, so on a wide screen
    // the whole block — logo, heading, cards, button — centres as one.
    <PublicFormShell brand={brand} tone="form" lockup="inline" lockupGap="tight">
      <PublicEnquiryFields
        handle={handle}
        form={form}
        submitting={submitting}
        submitError={submitError}
        fieldErrors={fieldErrors}
        onClearFieldError={clearFieldError}
        onSubmit={send}
      />
    </PublicFormShell>
  );
};

export default PublicEnquiryFormPage;
